import * as React from "react";
import { useMemo } from "react";
import { motion, useReducedMotion } from "motion/react";

import { DrawerSwipeHandle } from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
import { useWorkspaceDrawerWidth } from "./useWorkspaceDrawerWidth";
import {
  WORKSPACE_SHEET_MOTION,
  WORKSPACE_SHEET_TRANSITION,
} from "./workspaceOverlayMotion";
import type { WorkspaceExecutingAccountDrawerTarget } from "./workspaceDrawerEntry";
import { WorkspaceExecutingAccountDrawerContent } from "./drawer/WorkspaceExecutingAccountDrawerContent";

const WORKSPACE_DRAWER_WIDTH_TRANSITION =
  "width 360ms cubic-bezier(0.22, 1, 0.36, 1), right 360ms cubic-bezier(0.22, 1, 0.36, 1)";

const WORKSPACE_DRAWER_SIDE_BY_SIDE_GAP_PX = 12;

function stackPositionStyle(
  stackIndex: number,
  stackCount: number,
  width: number
): React.CSSProperties {
  if (stackCount <= 1) {
    return {
      width: `${width}px`,
      maxWidth: "100%",
      top: 0,
      right: 0,
      bottom: 0,
      position: "absolute",
    };
  }
  return {
    width: `${width}px`,
    maxWidth: "100%",
    position: "relative",
    height: "100%",
    flexShrink: 0,
  };
}

export type WorkspaceExecutingAccountDrawerPanelProps = {
  target: WorkspaceExecutingAccountDrawerTarget;
  open: boolean;
  stackIndex?: number;
  stackCount?: number;
  onOpenChange: (open: boolean) => void;
};

export function WorkspaceExecutingAccountDrawerPanel({
  target,
  open,
  stackIndex = 0,
  stackCount = 1,
  onOpenChange,
}: WorkspaceExecutingAccountDrawerPanelProps): React.ReactElement | null {
  const reduceMotion = useReducedMotion();
  const title = target.label.trim() || target.account.brokerAccountCode.trim() || "Executing account";

  const drawerWidthOptions = useMemo(() => {
    if (stackCount <= 1) return undefined;
    const host =
      typeof document !== "undefined"
        ? document.querySelector("[data-workspace-drawer-host]")
        : null;
    const hostWidth =
      host instanceof HTMLElement ? host.getBoundingClientRect().width : 0;
    if (hostWidth <= 0) return undefined;
    const half = Math.floor((hostWidth - WORKSPACE_DRAWER_SIDE_BY_SIDE_GAP_PX) / 2);
    return { maxWidthCap: half };
  }, [stackCount]);

  const { width, maxWidth, minWidth, setWidth, toggleFullWidth, isResizing, beginResize } =
    useWorkspaceDrawerWidth("summary", drawerWidthOptions);

  if (!open) return null;

  return (
    <motion.aside
      data-workspace-node-drawer=""
      data-workspace-node-drawer-type="executing-account"
      data-workspace-node-drawer-resizing={isResizing ? "" : undefined}
      role="dialog"
      aria-modal="false"
      aria-label={`${title} — Executing account`}
      style={{
        ...stackPositionStyle(stackIndex, stackCount, width),
        transition: isResizing ? undefined : WORKSPACE_DRAWER_WIDTH_TRANSITION,
      }}
      className={cn(
        "pointer-events-auto z-20 flex min-h-0 flex-col gap-0 overflow-visible",
        stackCount <= 1 ? "absolute" : "relative",
        "rounded-md border bg-popover text-sm text-popover-foreground shadow-none",
        isResizing && "transition-none duration-0"
      )}
      initial={reduceMotion ? false : WORKSPACE_SHEET_MOTION.initial}
      animate={WORKSPACE_SHEET_MOTION.animate}
      exit={reduceMotion ? undefined : WORKSPACE_SHEET_MOTION.exit}
      transition={
        isResizing
          ? { duration: 0 }
          : reduceMotion
            ? { duration: 0 }
            : WORKSPACE_SHEET_TRANSITION
      }
    >
      {stackCount <= 1 ? (
        <div
          data-workspace-node-drawer-resize=""
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize drawer. Double-click to expand to full width or restore previous width."
          aria-valuenow={width}
          aria-valuemin={minWidth}
          aria-valuemax={maxWidth}
          tabIndex={0}
          className={cn(
            "absolute inset-y-0 left-0 z-20 flex w-6 cursor-col-resize touch-none items-center justify-center overflow-hidden outline-none",
            "focus-visible:ring-2 focus-visible:ring-ring"
          )}
          onPointerDown={beginResize}
          onDoubleClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            toggleFullWidth();
          }}
          onKeyDown={(event) => {
            const step = event.shiftKey ? 48 : 16;
            if (event.key === "ArrowLeft") {
              event.preventDefault();
              setWidth(width + step);
            } else if (event.key === "ArrowRight") {
              event.preventDefault();
              setWidth(width - step);
            } else if (event.key === "Home") {
              event.preventDefault();
              setWidth(maxWidth);
            } else if (event.key === "End") {
              event.preventDefault();
              setWidth(minWidth);
            }
          }}
        >
          <DrawerSwipeHandle
            orientation="vertical"
            className="pointer-events-none h-full w-3 max-w-full cursor-col-resize"
          />
        </div>
      ) : null}

      <div
        className={cn(
          "min-h-0 flex-1 overflow-x-hidden overflow-y-auto rounded-[inherit] px-4 py-4",
          stackCount <= 1 ? "pl-6" : "pl-4"
        )}
      >
        <div className="flex min-h-full w-full min-w-0 flex-col">
          <WorkspaceExecutingAccountDrawerContent
            target={target}
            onClose={() => onOpenChange(false)}
          />
        </div>
      </div>
    </motion.aside>
  );
}
